import { styled, Typography, useTheme } from "@mui/material";
import { FunctionComponent } from "react";
import EarningsBox from "../EarningsBox";
import TileLogo from "./TileLogo";
import {
  StandardHeader,
  Props as BaseTileHeaderProps,
  Title,
  tileLogoMargin,
  useFilterTileNames,
} from "./StandardTileHeader";
import { getTileSpacing } from ".";
import { getHeaderItemPadding } from "./ElegantTileHeader";

const StackedHeader = styled(StandardHeader)(({ theme }) => ({
  flexWrap: "nowrap",
  alignItems: "flex-start",
  flexDirection: "column",
  paddingTop: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
  paddingLeft: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
  paddingRight: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
}));

export const StackedPartnerName = styled(Title)(({ theme }) => ({
  fontWeight: "normal",
  fontSize: theme.typography.body1.fontSize,
  marginBottom: theme.settings.tiles.spacing ? getTileSpacing(theme) : ".7rem",
  maxWidth: "100%",
}));

export const StackedDealName = styled(Title)(({ theme }) => ({
  flexBasis: "auto",
  maxWidth: "100%",
  color: theme.palette.primary.main,
  fontSize: theme.typography.h5.fontSize,
  width: 0,
  minWidth: "100%",
  display: "-webkit-box",
  WebkitLineClamp: `${
    theme.settings.tiles.titlesMultipleRows ? `2 !important` : `1 !important`
  }`,
}));

export const StackedSavingAmount = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.light,
  flexBasis: "auto",
  paddingLeft: getHeaderItemPadding(theme),
  paddingRight: getHeaderItemPadding(theme),
}));

const TileLogoContainer = styled("div")(() => ({
  marginBottom: tileLogoMargin,
}));

const StackedTileHeader: FunctionComponent<BaseTileHeaderProps> = ({
  id,
  item: { name, seller, deal },
}: BaseTileHeaderProps) => {
  const theme = useTheme();
  const {
    showVendorName,
    name: tileNameConfig,
    showSavingAmount,
  } = theme.settings.tiles.titleContent;
  const { dealName, listingName } = useFilterTileNames(
    tileNameConfig,
    name,
    deal?.title,
  );

  const logo = seller.symbolLogoUrl || seller.logoUrl;

  return (
    <StackedHeader id={id}>
      {logo && (
        <TileLogoContainer>
          <TileLogo
            logoUrl={logo}
            orgName={seller.name}
            width={50}
            height={50}
          />
        </TileLogoContainer>
      )}
      {showVendorName && (
        <StackedPartnerName variant="h3" noWrap title={seller.name}>
          {seller.name}
        </StackedPartnerName>
      )}
      {listingName && (
        <StackedDealName
          variant={showVendorName ? "h4" : "h3"}
          title={listingName}
        >
          {listingName}
        </StackedDealName>
      )}
      {dealName && (
        <StackedDealName
          variant={showVendorName ? "h4" : "h3"}
          title={dealName}
        >
          {dealName}
        </StackedDealName>
      )}

      {showSavingAmount && !!deal?.averageTotalSavingsAmount && (
        <StackedSavingAmount>
          <EarningsBox
            color={theme.palette.tileOthersColor.main}
            averageTotalSavingsAmount={deal.averageTotalSavingsAmount}
            averageTotalSavingsPrefix={deal.averageTotalSavingsPrefix}
            averageTotalSavingsCurrency={deal.averageTotalSavingsCurrency}
          />
        </StackedSavingAmount>
      )}
    </StackedHeader>
  );
};

export default StackedTileHeader;
